import PATHS from "./shared/constants/paths";
import { Navigate } from "react-router-dom";
import App from "./pages/App";
import DashboardPage from "pages/dashboard";
import SchedulePage from "pages/schedule";
import CustomersPage from "pages/customers";
import NotFoundPage from "pages/404";
import BusinessProfilePage from "pages/profile/business";
import UserProfilePage from "pages/profile/user";
import AuthLayout from "layout/AuthLayout";
import { AuthProviderWrapper } from "context/AuthContextProvider";
import CustomerDetails from "pages/customers/CustomerDetails";
import StaffsPage from "pages/instructors";
import StaffDetails from "pages/instructors/InstructorDetails";
import PackagesPage from "pages/products/packages";
import MembershipPage from "pages/products/membership";
import PackageProductDetailPage from "pages/products/package-detail";
import ClassPage from "pages/products/classtype";
import ClassTypeDetails from "pages/products/classtype/ClassTypeDetails";
import ScheduleDetails from "pages/schedule/ScheduleDetails";
import VerifyOTP from "pages/auth/VerifyOTP";
import ForgetPassword from "pages/auth/ForgetPassword";
import LogoutScreen from "pages/auth/Logout";
import LoginScreen from "pages/auth/Login";
import ResetPassword from "pages/auth/ResetPassword";
import BusinessPage from "pages/business";
import BusinessDetails from "pages/business/BusinessDetails";
import CustomerPackageDetailsPage from "pages/customers/CustomerPackageDetails";
import { ScheduleList } from "pages/schedule/view/list/ScheduleList";
import HomeRedirect from "shared/routes/HomeRedirect";
import AnalyticsIndexPage from "pages/analytics/AnalyticsIndexPage";
import ClassPerformancePage from "pages/analytics/ClassPerformancePage";
import PackageOverviewPage from "pages/analytics/PackageOverviewPage";
import PackageComparisonPage from "pages/analytics/PackageComparisonPage";
import PackageExpiryPage from "pages/analytics/PackageExpiryPage";
import PackagePurchasesPage from "pages/analytics/PackagePurchasesPage";
import MembershipOverviewPage from "pages/analytics/MembershipOverviewPage";
import MembershipComparisonPage from "pages/analytics/MembershipComparisonPage";
import MembershipExpiryPage from "pages/analytics/MembershipExpiryPage";
import MembershipPurchasesPage from "pages/analytics/MembershipPurchasesPage";
import SalesServicesPage from "pages/analytics/SalesServicesPage";
import SalesClientsPage from "pages/analytics/SalesClientsPage";
import SalesTrendPage from "pages/analytics/SalesTrendPage";
import SalesPackagesPage from "pages/analytics/SalesPackagesPage";
import SalesMembershipsPage from "pages/analytics/SalesMembershipsPage";
import SalesPackagesPromoPage from "pages/analytics/SalesPackagesPromoPage";
import SalesMembershipsPromoPage from "pages/analytics/SalesMembershipsPromoPage";
import MonthlyStatementPage from "pages/settings/MonthlyStatementPage";
import ClientPerformancePage from "pages/analytics/ClientPerformancePage";
import ClientInactivePage from "pages/analytics/ClientInactivePage";
import InstructorPerformancePage from "pages/analytics/InstructorPerformancePage";
import LoyaltyPerformancePage from "pages/analytics/LoyaltyPerformancePage";
import ReferralPerformancePage from "pages/analytics/ReferralPerformancePage";
import RewardUsagePage from "pages/analytics/RewardUsagePage";
import DiscountCodePerformancePage from "pages/analytics/DiscountCodePerformancePage";
import AccountsPage from "pages/accounts";
import PayrollSettingsPage from "pages/payroll";
import ClassCommissionPage from "pages/payroll/ClassCommissionPage";
import RunPayrollStartPage from "pages/payroll/RunPayrollStartPage";
import PayrollRunPage from "pages/payroll/PayrollRunPage";
import PayrollHistoryPage from "pages/payroll/PayrollHistoryPage";
import SettingsPage from "pages/settings";
import BookingPoliciesPage from "pages/settings/policy/BookingPoliciesPage";
import EmployeeManagementPage from "pages/settings/policy/EmployeeManagementPage";
import PaymentMethodsPage from "pages/settings/PaymentMethodsPage";
import TaxSettingsPage from "pages/settings/TaxSettingsPage";
import WhatsappContactPage from "pages/settings/WhatsappContactPage";
import NotificationSettingsPage from "pages/settings/NotificationSettingsPage";
import LoyaltyProgramPage from "pages/settings/LoyaltyProgramPage";
import ReferralProgramPage from "pages/settings/ReferralProgramPage";
import DiscountCodesPage from "pages/settings/DiscountCodesPage";
import AdminRestrictionsPage from "pages/settings/AdminRestrictionsPage";
import RequireRole from "shared/routes/RequireRole";
import { RequireCapability } from "components/capability";
import { AdminCapability } from "shared/utils/capabilities";
import { UserType } from "shared/types/enum";

const routes = [
  {
    element: <AuthProviderWrapper />,
    children: [
      {
        path: PATHS.HOME,
        element: <App />,
        children: [
          {
            index: true,
            element: <HomeRedirect />,
          },
          {
            path: PATHS.DASHBOARD,
            element: <DashboardPage />,
          },
          {
            path: PATHS.BUSINESS,
            element: <BusinessPage />,
          },
          {
            path: `${PATHS.BUSINESS}/:businessId`,
            element: <BusinessDetails />,
          },
          {
            path: PATHS.SCHEDULE,
            element: <SchedulePage />,
          },
          {
            path: PATHS.LIST,
            element: <ScheduleList />,
          },
          {
            path: `${PATHS.SCHEDULE}/:scheduleId`,
            element: <ScheduleDetails />,
          },
          {
            path: PATHS.CUSTOMER,
            element: <CustomersPage />,
          },
          {
            path: `${PATHS.CUSTOMER}/:customerId`,
            element: <CustomerDetails />,
          },
          {
            path: `${PATHS.CUSTOMER_PACKAGE}/:customerPackageId`,
            element: <CustomerPackageDetailsPage />,
          },
          {
            // The old single list; Instruktur is the first Karyawan sub-item.
            path: PATHS.STAFF,
            element: <Navigate to={PATHS.KARYAWAN_INSTRUCTORS} replace />,
          },
          {
            path: PATHS.KARYAWAN_INSTRUCTORS,
            element: <StaffsPage type={UserType.Instructor} />,
          },
          {
            path: PATHS.KARYAWAN_STAFF,
            element: <StaffsPage type={UserType.Staff} />,
          },
          {
            path: PATHS.ACCOUNTS,
            element: (
              <RequireRole allow={[UserType.Manager]}>
                <AccountsPage />
              </RequireRole>
            ),
          },
          {
            path: PATHS.PAYROLL,
            element: (
              <RequireRole allow={[UserType.Manager]}>
                <PayrollSettingsPage />
              </RequireRole>
            ),
          },
          {
            path: `${PATHS.PAYROLL_CLASS_RATE}/:classTypeId`,
            element: (
              <RequireRole allow={[UserType.Manager]}>
                <ClassCommissionPage />
              </RequireRole>
            ),
          },
          {
            path: PATHS.PAYROLL_RUN_NEW,
            element: (
              <RequireRole allow={[UserType.Manager]}>
                <RunPayrollStartPage />
              </RequireRole>
            ),
          },
          {
            path: `${PATHS.PAYROLL_RUN}/:runId`,
            element: (
              <RequireRole allow={[UserType.Manager]}>
                <PayrollRunPage />
              </RequireRole>
            ),
          },
          {
            path: PATHS.PAYROLL_HISTORY,
            element: (
              <RequireRole allow={[UserType.Manager]}>
                <PayrollHistoryPage />
              </RequireRole>
            ),
          },
          {
            path: PATHS.SETTINGS,
            element: <SettingsPage />,
          },
          {
            path: PATHS.SETTINGS_POLICIES,
            element: <BookingPoliciesPage />,
          },
          // The six rules had a page each until 2026-10-07; their old links
          // open the merged page at the rule's section.
          ...[
            [PATHS.SETTINGS_CANCELLATION, "cancellation"],
            [PATHS.SETTINGS_WAITLIST, "waitlist"],
            [PATHS.SETTINGS_VISIBILITY, "visibility"],
            [PATHS.SETTINGS_SCHEDULE_CONFLICT, "schedule-conflict"],
            [PATHS.SETTINGS_AUTO_COMPLETE, "auto-complete"],
            [PATHS.SETTINGS_VALIDITY_START, "validity-start"],
          ].map(([path, section]) => ({
            path,
            element: (
              <Navigate to={{ pathname: PATHS.SETTINGS_POLICIES, hash: `#${section}` }} replace />
            ),
          })),
          {
            path: PATHS.SETTINGS_PAYMENT_METHODS,
            element: <PaymentMethodsPage />,
          },
          {
            path: PATHS.SETTINGS_WHATSAPP,
            element: <WhatsappContactPage />,
          },
          {
            path: PATHS.SETTINGS_TAX,
            element: <TaxSettingsPage />,
          },
          {
            path: PATHS.SETTINGS_MONTHLY_STATEMENT,
            element: (
              <RequireCapability
                capability={AdminCapability.FinancialAnalytics}
                fallback={PATHS.SETTINGS}
              >
                <MonthlyStatementPage />
              </RequireCapability>
            ),
          },
          {
            path: PATHS.SETTINGS_NOTIFICATIONS,
            element: <NotificationSettingsPage />,
          },
          {
            // Reward program configs are open to any Admin (Andrew,
            // 2026-08-20 — Manager-only at launch, server gate lifted too);
            // since Pembatasan Admin every /settings/* page renders read-only
            // for an Admin whose Manager restricted that page's settings group
            // (booking & schedule policy / promotions & rewards / the rest).
            path: PATHS.SETTINGS_LOYALTY,
            element: <LoyaltyProgramPage />,
          },
          {
            path: PATHS.SETTINGS_REFERRAL,
            element: <ReferralProgramPage />,
          },
          {
            path: PATHS.SETTINGS_DISCOUNT_CODES,
            element: <DiscountCodesPage />,
          },
          {
            path: PATHS.SETTINGS_EMPLOYEE_MANAGEMENT,
            element: <EmployeeManagementPage />,
          },
          {
            // Pembatasan Admin: Manager-only by construction (an Admin who
            // could edit the toggles would make every other gate decorative).
            path: PATHS.SETTINGS_ADMIN_RESTRICTIONS,
            element: (
              <RequireRole allow={[UserType.Manager]}>
                <AdminRestrictionsPage />
              </RequireRole>
            ),
          },
          {
            path: `${PATHS.STAFF}/:instructorId`,
            element: <StaffDetails />,
          },
          {
            path: PATHS.CLASS,
            element: <ClassPage />,
          },
          {
            path: `${PATHS.CLASS}/:classTypeId`,
            element: <ClassTypeDetails />,
          },
          {
            path: PATHS.PACKAGE,
            element: <PackagesPage />,
          },
          {
            path: PATHS.PACKAGE_NEW,
            element: (
              <RequireCapability
                capability={AdminCapability.CatalogPricing}
                fallback={PATHS.PACKAGE}
              >
                <PackageProductDetailPage />
              </RequireCapability>
            ),
          },
          {
            path: `${PATHS.PACKAGE}/:productId`,
            element: <PackageProductDetailPage />,
          },
          {
            path: PATHS.MEMBERSHIP,
            element: <MembershipPage />,
          },
          {
            path: PATHS.MEMBERSHIP_NEW,
            element: (
              <RequireCapability
                capability={AdminCapability.CatalogPricing}
                fallback={PATHS.MEMBERSHIP}
              >
                <PackageProductDetailPage />
              </RequireCapability>
            ),
          },
          {
            // Membership product detail keeps its own path so the sidebar's
            // Membership item stays highlighted (same detail component).
            path: `${PATHS.MEMBERSHIP}/:productId`,
            element: <PackageProductDetailPage />,
          },
          {
            // Every report lives on this one page; the seven per-section
            // routes below it are redirects kept for old bookmarks.
            path: PATHS.ANALYTICS,
            element: <AnalyticsIndexPage />,
          },
          {
            path: PATHS.ANALYTICS_CLASS,
            element: <Navigate to={PATHS.ANALYTICS} replace />,
          },
          {
            path: PATHS.ANALYTICS_PACKAGE,
            element: <Navigate to={PATHS.ANALYTICS} replace />,
          },
          {
            path: PATHS.ANALYTICS_CLASS_PERFORMANCE,
            element: <ClassPerformancePage />,
          },
          {
            path: PATHS.ANALYTICS_PACKAGE_OVERVIEW,
            element: <PackageOverviewPage />,
          },
          {
            path: PATHS.ANALYTICS_PACKAGE_COMPARE,
            element: (
              <RequireCapability
                capability={AdminCapability.FinancialAnalytics}
                fallback={PATHS.ANALYTICS}
              >
                <PackageComparisonPage />
              </RequireCapability>
            ),
          },
          {
            path: PATHS.ANALYTICS_PACKAGE_EXPIRY,
            element: <PackageExpiryPage />,
          },
          {
            path: PATHS.ANALYTICS_PACKAGE_PURCHASES,
            element: (
              <RequireCapability
                capability={AdminCapability.FinancialAnalytics}
                fallback={PATHS.ANALYTICS}
              >
                <PackagePurchasesPage />
              </RequireCapability>
            ),
          },
          {
            path: PATHS.ANALYTICS_MEMBERSHIP,
            element: <Navigate to={PATHS.ANALYTICS} replace />,
          },
          {
            path: PATHS.ANALYTICS_MEMBERSHIP_OVERVIEW,
            element: <MembershipOverviewPage />,
          },
          {
            path: PATHS.ANALYTICS_MEMBERSHIP_COMPARE,
            element: (
              <RequireCapability
                capability={AdminCapability.FinancialAnalytics}
                fallback={PATHS.ANALYTICS}
              >
                <MembershipComparisonPage />
              </RequireCapability>
            ),
          },
          {
            path: PATHS.ANALYTICS_MEMBERSHIP_EXPIRY,
            element: <MembershipExpiryPage />,
          },
          {
            path: PATHS.ANALYTICS_MEMBERSHIP_PURCHASES,
            element: (
              <RequireCapability
                capability={AdminCapability.FinancialAnalytics}
                fallback={PATHS.ANALYTICS}
              >
                <MembershipPurchasesPage />
              </RequireCapability>
            ),
          },
          {
            // Pembatasan Admin: the money reports (Pendapatan family,
            // Pembelian, Bandingkan) redirect to the Laporan index for an
            // Admin whose Manager restricted financial analytics, and their
            // rows are left off that index. Operational reports stay open.
            // This route is now just an old bookmark: it redirects for
            // everyone, so it needs no capability guard of its own.
            path: PATHS.ANALYTICS_SALES,
            element: <Navigate to={PATHS.ANALYTICS} replace />,
          },
          {
            path: PATHS.ANALYTICS_SALES_SERVICES,
            element: (
              <RequireCapability
                capability={AdminCapability.FinancialAnalytics}
                fallback={PATHS.ANALYTICS}
              >
                <SalesServicesPage />
              </RequireCapability>
            ),
          },
          {
            path: PATHS.ANALYTICS_SALES_CLIENTS,
            element: (
              <RequireCapability
                capability={AdminCapability.FinancialAnalytics}
                fallback={PATHS.ANALYTICS}
              >
                <SalesClientsPage />
              </RequireCapability>
            ),
          },
          {
            path: PATHS.ANALYTICS_SALES_TREND,
            element: (
              <RequireCapability
                capability={AdminCapability.FinancialAnalytics}
                fallback={PATHS.ANALYTICS}
              >
                <SalesTrendPage />
              </RequireCapability>
            ),
          },
          {
            path: PATHS.ANALYTICS_SALES_PACKAGES,
            element: (
              <RequireCapability
                capability={AdminCapability.FinancialAnalytics}
                fallback={PATHS.ANALYTICS}
              >
                <SalesPackagesPage />
              </RequireCapability>
            ),
          },
          {
            path: PATHS.ANALYTICS_SALES_PACKAGES_PROMO,
            element: (
              <RequireCapability
                capability={AdminCapability.FinancialAnalytics}
                fallback={PATHS.ANALYTICS}
              >
                <SalesPackagesPromoPage />
              </RequireCapability>
            ),
          },
          {
            path: PATHS.ANALYTICS_SALES_MEMBERSHIPS,
            element: (
              <RequireCapability
                capability={AdminCapability.FinancialAnalytics}
                fallback={PATHS.ANALYTICS}
              >
                <SalesMembershipsPage />
              </RequireCapability>
            ),
          },
          {
            path: PATHS.ANALYTICS_SALES_MEMBERSHIPS_PROMO,
            element: (
              <RequireCapability
                capability={AdminCapability.FinancialAnalytics}
                fallback={PATHS.ANALYTICS}
              >
                <SalesMembershipsPromoPage />
              </RequireCapability>
            ),
          },
          {
            path: PATHS.ANALYTICS_CLIENT,
            element: <Navigate to={PATHS.ANALYTICS} replace />,
          },
          {
            path: PATHS.ANALYTICS_CLIENT_PERFORMANCE,
            element: <ClientPerformancePage />,
          },
          {
            path: PATHS.ANALYTICS_CLIENT_INACTIVE,
            element: <ClientInactivePage />,
          },
          {
            path: PATHS.ANALYTICS_INSTRUCTOR,
            element: <Navigate to={PATHS.ANALYTICS} replace />,
          },
          {
            path: PATHS.ANALYTICS_INSTRUCTOR_PERFORMANCE,
            element: <InstructorPerformancePage />,
          },
          {
            path: PATHS.ANALYTICS_REWARD,
            element: <Navigate to={PATHS.ANALYTICS} replace />,
          },
          {
            path: PATHS.ANALYTICS_REWARD_LOYALTY,
            element: <LoyaltyPerformancePage />,
          },
          {
            path: PATHS.ANALYTICS_REWARD_REFERRAL,
            element: <ReferralPerformancePage />,
          },
          {
            path: PATHS.ANALYTICS_REWARD_USAGE,
            element: <RewardUsagePage />,
          },
          {
            path: PATHS.ANALYTICS_REWARD_DISCOUNT_CODES,
            element: <DiscountCodePerformancePage />,
          },
          {
            path: PATHS.BUSINESS_PROFILE,
            element: <BusinessProfilePage />,
          },
          {
            path: PATHS.USER_PROFILE,
            element: <UserProfilePage />,
          },
          {
            path: PATHS.LOGOUT,
            element: <LogoutScreen />,
          },
          {
            path: "*",
            element: <NotFoundPage />,
          },
        ],
      },

      {
        element: <AuthLayout />,
        children: [
          {
            path: PATHS.LOGIN,
            element: <LoginScreen />,
          },
          {
            path: PATHS.VERIFICATION,
            element: <VerifyOTP />,
          },
          {
            path: PATHS.FORGET_PASSWORD,
            element: <ForgetPassword />,
          },
          {
            path: PATHS.RESET_PASSWORD,
            element: <ResetPassword />,
          },
        ],
      },
    ],
  },
];

export default routes;
